/**
 * A cozy animated campfire rendered to a full-window canvas: flickering flame
 * layers plus rising embers. Purely decorative; runs on requestAnimationFrame
 * and resizes with the window.
 */
interface Ember {
  x: number;
  y: number;
  vx: number;
  vy: number;
  life: number;
  maxLife: number;
  size: number;
}

export class Campfire {
  private ctx: CanvasRenderingContext2D;
  private raf = 0;
  private embers: Ember[] = [];
  private t = 0;
  private dpr = Math.min(globalThis.devicePixelRatio || 1, 2);
  private intensity = 1;
  private readonly onResize = () => this.resize();

  constructor(private readonly canvas: HTMLCanvasElement) {
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('2D canvas context unavailable');
    this.ctx = ctx;
    this.resize();
    globalThis.addEventListener('resize', this.onResize);
  }

  /** Scale the blaze: 1 = steady, higher = roaring (used to react to streaks). */
  setIntensity(value: number): void {
    this.intensity = Math.max(0.2, Math.min(2.5, value));
  }

  start(): void {
    if (this.raf) return;
    const loop = () => {
      this.step();
      this.raf = requestAnimationFrame(loop);
    };
    this.raf = requestAnimationFrame(loop);
  }

  stop(): void {
    if (this.raf) cancelAnimationFrame(this.raf);
    this.raf = 0;
    globalThis.removeEventListener('resize', this.onResize);
  }

  private resize(): void {
    const w = globalThis.innerWidth;
    const h = globalThis.innerHeight;
    this.canvas.width = Math.floor(w * this.dpr);
    this.canvas.height = Math.floor(h * this.dpr);
    this.canvas.style.width = `${w}px`;
    this.canvas.style.height = `${h}px`;
    this.ctx.setTransform(this.dpr, 0, 0, this.dpr, 0, 0);
  }

  private get baseX(): number {
    return globalThis.innerWidth / 2;
  }
  private get baseY(): number {
    return globalThis.innerHeight - Math.min(140, globalThis.innerHeight * 0.18);
  }

  private spawnEmber(): void {
    const spread = 26 * this.intensity;
    this.embers.push({
      x: this.baseX + (Math.random() - 0.5) * spread,
      y: this.baseY,
      vx: (Math.random() - 0.5) * 0.4,
      vy: -(0.6 + Math.random() * 1.4) * this.intensity,
      life: 0,
      maxLife: 60 + Math.random() * 90,
      size: 1 + Math.random() * 2.2,
    });
  }

  private step(): void {
    const { ctx } = this;
    const w = globalThis.innerWidth;
    const h = globalThis.innerHeight;
    this.t += 0.05;

    // Night sky wash with a warm pool of firelight near the base.
    const sky = ctx.createLinearGradient(0, 0, 0, h);
    sky.addColorStop(0, '#0b1020');
    sky.addColorStop(0.55, '#0e1326');
    sky.addColorStop(1, '#1a1410');
    ctx.fillStyle = sky;
    ctx.fillRect(0, 0, w, h);

    const glowR = (180 + Math.sin(this.t * 1.7) * 14) * this.intensity;
    const glow = ctx.createRadialGradient(
      this.baseX,
      this.baseY,
      0,
      this.baseX,
      this.baseY,
      glowR,
    );
    glow.addColorStop(0, 'rgba(255, 158, 66, 0.35)');
    glow.addColorStop(0.5, 'rgba(255, 120, 40, 0.12)');
    glow.addColorStop(1, 'rgba(255, 120, 40, 0)');
    ctx.fillStyle = glow;
    ctx.fillRect(0, 0, w, h);

    this.drawLogs();
    this.drawFlames();

    // Embers
    if (Math.random() < 0.6 * this.intensity) this.spawnEmber();
    ctx.globalCompositeOperation = 'lighter';
    for (const e of this.embers) {
      e.life += 1;
      e.x += e.vx;
      e.y += e.vy;
      e.vy -= 0.004; // gentle acceleration upward as it heats
      const k = 1 - e.life / e.maxLife;
      if (k <= 0) continue;
      ctx.fillStyle = `rgba(255, ${140 + Math.floor(80 * k)}, 60, ${k * 0.9})`;
      ctx.beginPath();
      ctx.arc(e.x, e.y, e.size * k, 0, Math.PI * 2);
      ctx.fill();
    }
    ctx.globalCompositeOperation = 'source-over';
    this.embers = this.embers.filter((e) => e.life < e.maxLife);
  }

  private drawLogs(): void {
    const { ctx } = this;
    const x = this.baseX;
    const y = this.baseY + 14;
    ctx.save();
    ctx.translate(x, y);
    for (const angle of [-0.35, 0.35]) {
      ctx.save();
      ctx.rotate(angle);
      ctx.fillStyle = '#3a2417';
      ctx.fillRect(-46, -7, 92, 14);
      ctx.fillStyle = '#5a3a22';
      ctx.fillRect(-46, -7, 14, 14);
      ctx.fillRect(32, -7, 14, 14);
      ctx.restore();
    }
    ctx.restore();
  }

  private drawFlames(): void {
    const { ctx } = this;
    const x = this.baseX;
    const y = this.baseY;
    const layers = [
      { color: 'rgba(196, 64, 24, 0.9)', h: 120, w: 42 },
      { color: 'rgba(255, 122, 28, 0.92)', h: 92, w: 32 },
      { color: 'rgba(255, 180, 60, 0.95)', h: 60, w: 20 },
      { color: 'rgba(255, 232, 150, 0.95)', h: 32, w: 11 },
    ];
    ctx.globalCompositeOperation = 'lighter';
    for (let i = 0; i < layers.length; i++) {
      const l = layers[i];
      const sway = Math.sin(this.t * (2 + i * 0.6) + i) * (6 - i) * this.intensity;
      const height = l.h * this.intensity * (0.9 + Math.sin(this.t * 3 + i) * 0.08);
      const width = l.w * this.intensity;
      ctx.fillStyle = l.color;
      ctx.beginPath();
      ctx.moveTo(x - width, y);
      ctx.quadraticCurveTo(x - width * 0.6 + sway, y - height * 0.5, x + sway, y - height);
      ctx.quadraticCurveTo(x + width * 0.6 + sway, y - height * 0.5, x + width, y);
      ctx.closePath();
      ctx.fill();
    }
    ctx.globalCompositeOperation = 'source-over';
  }
}
